import React, { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { Tabs } from '@components/atoms/Tabs';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Form } from '@components/atoms/Form';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Space } from '@components/atoms/Space';
import { Table } from '@components/atoms/Table';
import { Checkbox, CheckboxType } from '@components/atoms/Checkbox';
import { Select, SelectType } from '@components/atoms/Select';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { PopupDelete } from '@components/molecules/PopupDelete';
import { ProductForm } from '@components/molecules/ProductForm';
import { SITE_URLS } from '@constants';
import { usePermissionChecker, UserPermissions } from '@hooks/useHasPermission';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useData } from '@hooks/useData';
import { useNotifications } from '@hooks/useNotifications';
import { useDeleteProductImage } from '@services/useDeleteProductImage';
import { useAddProductImages } from '@services/useAddProductImages';
import { useGetMyAccountProductDetail } from '@services/useGetMyAccountProductDetail';
import { useUpdateProductAttributes } from '@services/useUpdateProductAttributes';
import { useDeleteProductVariant } from '@services/useDeleteProductVariant';
import { useAddProductVariant } from '@services/useAddProductVariant';
import { useUpdateProductDefaultVariant } from '@services/useUpdateProductDefaultVariant';
import { InputErrors } from '@types';
import { formatPrice } from '@utils/format';
import { convertFilesToBase64 } from '@utils/image';
import styles from './manageProductsEdit.module.css';

export const ManageProductsEdit: FC = () => {
    const { navigate } = useCustomNavigate();
    const { can } = usePermissionChecker();
    const { myAccountProductDetail } = useData();
    const { t } = useTranslation();

    const translations = {
        title: t('manage-products-edit-component.title', { name: myAccountProductDetail?.data?.product?.name || '' }),
        productsTab: t('manage-products-form.products.tab'),
        attributesTab: t('manage-products-form.attributes.tab'),
        pricesTab: t('manage-products-form.prices.tab'),
        imagesTab: t('manage-products-form.images.tab'),
    };

    // if user does not have permission accessMerchantAreas, he can't access this page
    useEffect(() => {
        if (can(UserPermissions.accessMerchantAreas) === false) {
            navigate(`/${SITE_URLS.myProfile}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <MyAccountContainer title={translations.title}>
            <>
                <div className="pt-4">
                    <Tabs
                        tabs={[
                            { label: translations.productsTab, content: <ProductForm edit={true} /> },
                            { label: translations.attributesTab, content: <AttributesTab /> },
                            { label: translations.pricesTab, content: <PricesTab /> },
                            { label: translations.imagesTab, content: <ImagesTab /> },
                        ]}
                    />
                </div>
            </>
        </MyAccountContainer>
    );
};

const AttributesTab: FC = () => {
    const { showDefaultNotification } = useNotifications();
    const { request: requestUpdateProductAttributes, error, success, lastResponseTime } = useUpdateProductAttributes();
    const { request: requestMyAccountProductDetail } = useGetMyAccountProductDetail();
    const { myAccountProductDetail } = useData();
    const { t } = useTranslation();

    const translations = {
        emptyState: t('manage-products-edit-component.attributes.empty-state'),
        submitButton: t('manage-products-form.submit.button'),
    };

    const [selectedAttributes, setSelectedAttributes] = useState<{ attributeUuid?: string; valueUuid?: string }[]>([]);

    useEffect(() => {
        setSelectedAttributes(
            myAccountProductDetail?.data?.productAttributes?.flatMap((attribute) =>
                (attribute.values || []).map((value) => ({ attributeUuid: attribute.uuid, valueUuid: value.uuid }))
            ) || []
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myAccountProductDetail]);

    const handleOnToggleAttribute = (valueUuid: string, attributeUuid: string) => {
        setSelectedAttributes((previous) => {
            const isSelected =
                previous.find((item) => item.valueUuid === valueUuid && item.attributeUuid === attributeUuid) !== undefined;

            if (isSelected) {
                // remove
                return [...previous.filter((item) => !(item.valueUuid === valueUuid && item.attributeUuid === attributeUuid))];
            } else {
                // add
                return [...previous, { attributeUuid, valueUuid }];
            }
        });
    };

    const formRef = useRef<HTMLFormElement>(null);

    const submitForm: () => void = async () => {
        requestUpdateProductAttributes({ uuid: myAccountProductDetail?.data?.uuid || '' }, { attributes: selectedAttributes });
    };

    // dispatch notification, according to feedback of service
    useEffect(() => {
        if (lastResponseTime) {
            if (error) {
                showDefaultNotification({ type: 'error', message: error });
            } else {
                showDefaultNotification({ type: 'success', message: success || '' });
                // update
                requestMyAccountProductDetail({ uuid: myAccountProductDetail?.data?.uuid || '' });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    return (
        <Form formRef={formRef} onSubmit={() => submitForm()}>
            <>
                {myAccountProductDetail?.data?.attributes && myAccountProductDetail.data.attributes.length > 0 ? (
                    <>
                        {myAccountProductDetail.data.attributes.map((attribute) => {
                            const attributeValues = selectedAttributes
                                .filter((item) => item.attributeUuid === attribute.uuid)
                                .map((item) => item.valueUuid);

                            return (
                                <Fragment key={`attribute-${attribute.uuid}`}>
                                    {attribute.values && attribute.values.length > 0 && (
                                        <>
                                            <div>
                                                <Typography fontWeight="800">{attribute.label}</Typography>
                                                <div className="mt-3 d-flex column-gap-4 row-gap-1 flex-wrap">
                                                    {attribute.values.map((value) => {
                                                        const checked = attributeValues.includes(value.uuid) || false;

                                                        return (
                                                            <Checkbox
                                                                key={`value-${value.uuid}`}
                                                                type={CheckboxType.normal}
                                                                checked={checked}
                                                                name={value.uuid || ''}
                                                                label={value.label || ''}
                                                                value={value.uuid || ''}
                                                                onChange={() =>
                                                                    handleOnToggleAttribute(
                                                                        value.uuid || '',
                                                                        attribute.uuid || ''
                                                                    )
                                                                }
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                            <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                                        </>
                                    )}
                                </Fragment>
                            );
                        })}

                        {/* submit */}
                        <div className="col-12 text-end">
                            <Button
                                icon="fa-solid fa-floppy-disk"
                                label={translations.submitButton}
                                mode={ButtonMode.primary}
                                onClick={() => formRef?.current?.requestSubmit()}
                            />
                        </div>
                    </>
                ) : (
                    <>{translations.emptyState}</>
                )}
            </>
        </Form>
    );
};

const PricesTab: FC = () => {
    return (
        <>
            <DefaultVariantForm />
            <AddVariantForm />
        </>
    );
};

interface EditImagesFormErrors {
    images: InputErrors;
}

const ImagesTab: FC = () => {
    const { showDefaultNotification } = useNotifications();
    const { myAccountProductDetail } = useData();
    const {
        request: requestUpload,
        loading,
        success: successUpload,
        errorMessage,
        validationErrors,
        lastResponseTime: lastUpload,
    } = useAddProductImages();
    const { request: requestMyAccountProductDetail } = useGetMyAccountProductDetail();
    const { t } = useTranslation();

    const translations = {
        imagesLabel: t('manage-products-edit-component.images.label'),
        imagesRequiredError: t('manage-products-edit-component.images.required-error'),
        submitButton: t('manage-products-form.submit.button'),
    };

    const formRef = useRef<HTMLFormElement>(null);
    const [images, setImages] = useState<File[]>([]);

    // force the input to reset
    const [imagesInputKey, setImagesInputKey] = useState(Date.now());

    const handleReset = () => {
        setImagesInputKey(Date.now());
    };

    // errors to be displayed in the form
    const [errors, setErrors] = useState<EditImagesFormErrors>({
        images: [],
    });

    const handleOnClickSubmitImages = () => {
        const errors = validateForm();

        if (errors.images.length === 0) {
            formRef?.current?.requestSubmit();
        }
    };

    const validateForm: () => EditImagesFormErrors = () => {
        const newErrors: EditImagesFormErrors = {
            images: [],
        };

        images.length === 0 && newErrors.images.push(translations.imagesRequiredError);

        setErrors(newErrors);

        return newErrors;
    };

    const submitForm: () => void = async () => {
        const imageInput = document.getElementById('images-input') as HTMLInputElement;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const base64Images: any = imageInput.files ? await convertFilesToBase64(imageInput.files) : null;

        requestUpload({
            product_uuid: myAccountProductDetail?.data?.uuid,
            image: base64Images || [],
        });
    };

    // dispatch notification, according to feedback of adding images
    useEffect(() => {
        if (lastUpload) {
            if (errorMessage) {
                showDefaultNotification({ type: 'error', message: errorMessage });
            } else if (validationErrors) {
                const firstImageErrorKey = Object.keys(validationErrors).find((key) => key.startsWith('image'));
                setErrors({
                    // match form request errors with frontend errors object
                    images: firstImageErrorKey && (validationErrors?.[firstImageErrorKey] || []),
                });
            } else {
                showDefaultNotification({ type: 'success', message: successUpload || '' });
                // update images input
                handleReset();
                setImages([]);
                requestMyAccountProductDetail({ uuid: myAccountProductDetail?.data?.uuid || '' });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastUpload]);

    return (
        <Form formRef={formRef} onSubmit={() => submitForm()}>
            {/* new images */}
            <div className="row">
                <div className="col-md-6 col-12">
                    <Input
                        key={imagesInputKey}
                        id="images-input"
                        type={InputTypes.file}
                        label={`${translations.imagesLabel}*`}
                        variant={InputVariants.labeledInput}
                        multiple={true}
                        value=""
                        onChange={(value: FileList | null) => {
                            const imagesArray: File[] = Array.from(value || []);
                            setImages(imagesArray || []);
                        }}
                        state={errors.images.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.images}
                    />
                </div>

                {/* submit */}
                <div className="col-md-6 col-12 pt-1 text-end">
                    <Button
                        icon="fa-solid fa-floppy-disk"
                        label={translations.submitButton}
                        mode={ButtonMode.primary}
                        onClick={() => handleOnClickSubmitImages()}
                        disabled={loading}
                    />
                </div>
            </div>

            <Space spaceBreakpoints={{ xs: { space: 15 } }} />

            {/* current images */}
            <div className="row">
                <ImagesTable />
            </div>
        </Form>
    );
};

interface DefaultVariantFormErrors {
    sku: InputErrors;
    price: InputErrors;
    discount: InputErrors;
    quantity: InputErrors;
}

const DefaultVariantForm: FC = () => {
    const { showDefaultNotification } = useNotifications();
    const {
        request: requestUpdateProductDefaultVariant,
        errorMessage,
        validationErrors,
        success,
        lastResponseTime,
    } = useUpdateProductDefaultVariant();
    const { request: requestMyAccountProductDetail } = useGetMyAccountProductDetail();
    const { myAccountProductDetail } = useData();
    const { t } = useTranslation();

    const translations = {
        title: t('manage-products-edit-component.default-variant.title'),
        skuLabel: t('manage-products-edit-component.variants.inputs.sku.label'),
        priceLabel: t('manage-products-edit-component.variants.inputs.price.label'),
        discountLabel: t('manage-products-edit-component.variants.inputs.discount.label'),
        quantityLabel: t('manage-products-edit-component.variants.inputs.quantity.label'),
        submitButton: t('manage-products-form.submit.button'),
        priceRequiredError: t('manage-products-edit-component.variants.inputs.price.required-error'),
        discountRequiredError: t('manage-products-edit-component.variants.inputs.discount.required-error'),
        quantityRequiredError: t('manage-products-edit-component.variants.inputs.quantity.required-error'),
    };

    const formRef = useRef<HTMLFormElement>(null);

    const [sku, setSku] = useState<string>('');
    const [price, setPrice] = useState<number | undefined>();
    const [discount, setDiscount] = useState<number>(0);
    const [quantity, setQuantity] = useState<number | undefined>();

    // initially, update fields
    useEffect(() => {
        setSku(myAccountProductDetail?.data?.defaultVariant?.sku || '');
        setPrice(myAccountProductDetail?.data?.defaultVariant?.price);
        setDiscount(myAccountProductDetail?.data?.defaultVariant?.discount_price || 0);
        setQuantity(myAccountProductDetail?.data?.defaultVariant?.quantity);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myAccountProductDetail]);

    // errors to be displayed in the form
    const [errors, setErrors] = useState<DefaultVariantFormErrors>({
        sku: [],
        price: [],
        discount: [],
        quantity: [],
    });

    const handleOnClickUpdateDefaultVariant = () => {
        const errors = validateForm();

        if (
            errors.sku.length === 0 &&
            errors.price.length === 0 &&
            errors.discount.length === 0 &&
            errors.quantity.length === 0
        ) {
            formRef?.current?.requestSubmit();
        }
    };

    const validateForm: () => DefaultVariantFormErrors = () => {
        const newErrors: DefaultVariantFormErrors = {
            sku: [],
            price: [],
            discount: [],
            quantity: [],
        };

        (price === undefined || `${price}`.trim() === '') && newErrors.price.push(translations.priceRequiredError);
        `${discount}`.trim() === '' && newErrors.discount.push(translations.discountRequiredError);
        !quantity && newErrors.quantity.push(translations.quantityRequiredError);

        setErrors(newErrors);

        return newErrors;
    };

    const submitForm: () => void = async () => {
        requestUpdateProductDefaultVariant(
            { uuid: myAccountProductDetail?.data?.uuid || '' },
            {
                sku: sku,
                price: price,
                discount_price: discount,
                quantity: quantity,
            }
        );
    };

    // dispatch notification, according to feedback of service
    useEffect(() => {
        if (lastResponseTime) {
            if (errorMessage) {
                showDefaultNotification({ type: 'error', message: errorMessage });
            } else if (validationErrors) {
                setErrors({
                    // match form request errors with frontend errors object
                    sku: validationErrors?.sku || [],
                    price: validationErrors?.price || [],
                    discount: validationErrors?.discount_price || [],
                    quantity: validationErrors?.quantity || [],
                });
            } else {
                showDefaultNotification({ type: 'success', message: success || '' });
                // update
                requestMyAccountProductDetail({ uuid: myAccountProductDetail?.data?.uuid || '' });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    return (
        <Form formRef={formRef} onSubmit={() => submitForm()}>
            <>
                <Typography fontWeight="800">{translations.title}</Typography>

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                <div className="row mb-3">
                    <div className="col-12 col-lg-6 col-xl-3">
                        <Input
                            type={InputTypes.text}
                            variant={InputVariants.labeledInput}
                            label={translations.skuLabel}
                            value={sku}
                            onChange={(value) => setSku(value)}
                            state={errors.sku.length > 0 ? InputStates.error : undefined}
                            errorMessages={errors.sku}
                        />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </div>
                    <div className="col-12 col-lg-6 col-xl-3">
                        <Input
                            type={InputTypes.number}
                            variant={InputVariants.labeledInput}
                            label={`${translations.priceLabel}*`}
                            step={0.01}
                            value={`${price}`}
                            onChange={(value) => setPrice(value)}
                            state={errors.price.length > 0 ? InputStates.error : undefined}
                            // display only the first error
                            errorMessages={errors.price.length > 0 ? [errors.price[0]] : []}
                        />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </div>
                    <div className="col-12 col-lg-6 col-xl-3">
                        <Input
                            type={InputTypes.number}
                            variant={InputVariants.labeledInput}
                            label={`${translations.discountLabel}*`}
                            step={0.01}
                            value={`${discount}`}
                            onChange={(value) => setDiscount(value)}
                            state={errors.discount.length > 0 ? InputStates.error : undefined}
                            // display only the first error
                            errorMessages={errors.discount.length > 0 ? [errors.discount[0]] : []}
                        />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </div>
                    <div className="col-12 col-lg-6 col-xl-3">
                        <Input
                            type={InputTypes.number}
                            variant={InputVariants.labeledInput}
                            label={`${translations.quantityLabel}*`}
                            value={`${quantity}`}
                            onChange={(value) => setQuantity(value)}
                            state={errors.quantity.length > 0 ? InputStates.error : undefined}
                            // display only the first error
                            errorMessages={errors.quantity.length > 0 ? [errors.quantity[0]] : []}
                        />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </div>

                    {/* submit */}
                    <div className="col-12 text-end">
                        <Button
                            label={translations.submitButton}
                            icon="fa-solid fa-floppy-disk"
                            size="sm"
                            mode={ButtonMode.primary}
                            onClick={() => handleOnClickUpdateDefaultVariant()}
                        />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </div>
                </div>
            </>
        </Form>
    );
};

interface AddVariantFormErrors {
    attributes: InputErrors;
    sku: InputErrors;
    price: InputErrors;
    discount: InputErrors;
    quantity: InputErrors;
}

const AddVariantForm: FC = () => {
    const { showDefaultNotification } = useNotifications();
    const {
        request: requestAddProductVariant,
        errorMessage,
        validationErrors,
        success,
        lastResponseTime,
    } = useAddProductVariant();
    const { request: requestMyAccountProductDetail } = useGetMyAccountProductDetail();
    const { myAccountProductDetail } = useData();
    const { t } = useTranslation();

    const translations = {
        title: t('manage-products-edit-component.add-variant.title'),
        skuLabel: t('manage-products-edit-component.variants.inputs.sku.label'),
        priceLabel: t('manage-products-edit-component.variants.inputs.price.label'),
        discountLabel: t('manage-products-edit-component.variants.inputs.discount.label'),
        quantityLabel: t('manage-products-edit-component.variants.inputs.quantity.label'),
        submitButton: t('manage-products-edit-component.add-variant.submit.button'),
        default: t('select-default-option'),
        priceRequiredError: t('manage-products-edit-component.variants.inputs.price.required-error'),
        discountRequiredError: t('manage-products-edit-component.variants.inputs.discount.required-error'),
        quantityRequiredError: t('manage-products-edit-component.variants.inputs.quantity.required-error'),
        attributeRequiredError: t('manage-products-edit-component.variants.inputs.attribute.required-error'),
    };

    const formRef = useRef<HTMLFormElement>(null);

    const [selectedAttributes, setSelectedAttributes] = useState<{ attributeUuid?: string; valueUuid?: string }[]>([]);
    const [sku, setSku] = useState<string>('');
    const [price, setPrice] = useState<number | undefined>();
    const [discount, setDiscount] = useState<number>(0);
    const [quantity, setQuantity] = useState<number | undefined>();

    // errors to be displayed in the form
    const [errors, setErrors] = useState<AddVariantFormErrors>({
        attributes: [],
        sku: [],
        price: [],
        discount: [],
        quantity: [],
    });

    const handleOnSelectAttribute = (valueUuid: string, attributeUuid: string) => {
        setSelectedAttributes((previous) => {
            const otherAttributes = previous.filter((item) => item.attributeUuid !== attributeUuid);

            // if no value was selected
            // remove attribute
            if (valueUuid === '') {
                return [...otherAttributes];
            }

            // else, add it
            return [...otherAttributes, { attributeUuid, valueUuid }];
        });
    };

    const handleOnClickAddVariant = () => {
        const errors = validateForm();

        if (
            errors.sku.length === 0 &&
            errors.price.length === 0 &&
            errors.discount.length === 0 &&
            errors.quantity.length === 0 &&
            errors.attributes.length === 0
        ) {
            formRef?.current?.requestSubmit();
        }
    };

    const validateForm: () => AddVariantFormErrors = () => {
        const newErrors: AddVariantFormErrors = {
            attributes: [],
            sku: [],
            price: [],
            discount: [],
            quantity: [],
        };

        (price === undefined || `${price}`.trim() === '') && newErrors.price.push(translations.priceRequiredError);
        `${discount}`.trim() === '' && newErrors.discount.push(translations.discountRequiredError);
        !quantity && newErrors.quantity.push(translations.quantityRequiredError);
        selectedAttributes.length === 0 && newErrors.attributes.push(translations.attributeRequiredError);

        setErrors(newErrors);

        return newErrors;
    };

    const submitForm: () => void = async () => {
        requestAddProductVariant({
            product_uuid: myAccountProductDetail?.data?.uuid || '',
            sku_v: sku,
            price_v: price,
            discount_price_v: discount,
            quantity_v: quantity,
            attribute_values: selectedAttributes.map((item) => item.valueUuid || '') || [],
        });
    };

    const clearForm = () => {
        setSku('');
        setPrice(undefined);
        setDiscount(0);
        setQuantity(undefined);
        setSelectedAttributes([]);
    };

    // dispatch notification, according to feedback of adding variant
    useEffect(() => {
        if (lastResponseTime) {
            if (errorMessage) {
                showDefaultNotification({ type: 'error', message: errorMessage });
            } else if (validationErrors) {
                setErrors({
                    // match form request errors with frontend errors object
                    sku: validationErrors?.sku_v || [],
                    price: validationErrors?.price_v || [],
                    discount: validationErrors?.discount_price_v || [],
                    quantity: validationErrors?.quantity_v || [],
                    attributes: validationErrors?.attribute_values || [],
                });
            } else {
                showDefaultNotification({ type: 'success', message: success || '' });
                clearForm();
                requestMyAccountProductDetail({ uuid: myAccountProductDetail?.data?.uuid || '' });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    return (
        <>
            {
                // has default price already set
                myAccountProductDetail?.data?.defaultVariant?.uuid &&
                // marked as having variants
                myAccountProductDetail?.data?.product?.has_variants !== '0' &&
                // has attributes
                myAccountProductDetail?.data?.productAttributes &&
                myAccountProductDetail?.data?.productAttributes.length > 0 ? (
                    <>
                        <Form formRef={formRef} onSubmit={() => submitForm()}>
                            <>
                                <Typography fontWeight="800">{translations.title}</Typography>

                                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                                <div className="row mb-3">
                                    {myAccountProductDetail.data.productAttributes.map((attribute) => (
                                        <Fragment key={`attribute-${attribute.uuid}`}>
                                            {attribute.values && (
                                                <div className="col col-auto">
                                                    <div>
                                                        <Typography color="muted" fontSize="12">
                                                            <>{attribute.label}</>
                                                        </Typography>
                                                        <Select
                                                            type={SelectType.variant1}
                                                            options={[
                                                                { value: '', text: translations.default },
                                                                ...attribute.values.map((item) => {
                                                                    return {
                                                                        value: item.uuid || '',
                                                                        text: item.label || '',
                                                                    };
                                                                }),
                                                            ]}
                                                            value={
                                                                selectedAttributes.find(
                                                                    (item) => item.attributeUuid === attribute.uuid
                                                                )?.valueUuid || ''
                                                            }
                                                            onChange={(value) =>
                                                                handleOnSelectAttribute(value, attribute.uuid || '')
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </Fragment>
                                    ))}
                                    {errors.attributes.length > 0 && <ErrorMessages messages={errors.attributes} />}
                                </div>

                                <div className="row">
                                    <div className="col-12 col-lg-6 col-xl-3">
                                        <Input
                                            type={InputTypes.text}
                                            variant={InputVariants.labeledInput}
                                            label={translations.skuLabel}
                                            value={sku}
                                            onChange={(value) => setSku(value)}
                                            state={errors.sku.length > 0 ? InputStates.error : undefined}
                                            errorMessages={errors.sku}
                                        />
                                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                                    </div>
                                    <div className="col-12 col-lg-6 col-xl-3">
                                        <Input
                                            type={InputTypes.number}
                                            variant={InputVariants.labeledInput}
                                            label={`${translations.priceLabel}*`}
                                            step={0.01}
                                            value={`${price}`}
                                            onChange={(value) => setPrice(value)}
                                            state={errors.price.length > 0 ? InputStates.error : undefined}
                                            // display only the first error
                                            errorMessages={errors.price.length > 0 ? [errors.price[0]] : []}
                                        />
                                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                                    </div>
                                    <div className="col-12 col-lg-6 col-xl-3">
                                        <Input
                                            type={InputTypes.number}
                                            variant={InputVariants.labeledInput}
                                            label={`${translations.discountLabel}*`}
                                            step={0.01}
                                            value={`${discount}`}
                                            onChange={(value) => setDiscount(value)}
                                            state={errors.discount.length > 0 ? InputStates.error : undefined}
                                            // display only the first error
                                            errorMessages={errors.discount.length > 0 ? [errors.discount[0]] : []}
                                        />
                                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                                    </div>
                                    <div className="col-12 col-lg-6 col-xl-3">
                                        <Input
                                            type={InputTypes.number}
                                            variant={InputVariants.labeledInput}
                                            label={`${translations.quantityLabel}*`}
                                            value={`${quantity}`}
                                            onChange={(value) => setQuantity(value)}
                                            state={errors.quantity.length > 0 ? InputStates.error : undefined}
                                            // display only the first error
                                            errorMessages={errors.quantity.length > 0 ? [errors.quantity[0]] : []}
                                        />
                                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                                    </div>

                                    {/* submit */}
                                    <div className="col-12 text-end">
                                        <Button
                                            label={translations.submitButton}
                                            icon="fa-light fa-plus"
                                            size="sm"
                                            mode={ButtonMode.primary}
                                            onClick={() => handleOnClickAddVariant()}
                                        />
                                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                                    </div>
                                </div>
                            </>
                        </Form>
                        <VariantsTable />
                    </>
                ) : (
                    <></>
                )
            }
        </>
    );
};

const VariantsTable: FC = () => {
    const { showDefaultNotification } = useNotifications();
    const { request: requestDelete, loading, error: errorDelete, lastResponseTime } = useDeleteProductVariant();
    const { myAccountProductDetail } = useData();
    const { t } = useTranslation();

    const translations = {
        attributes: t('manage-products-edit-component.variants.header.attributes'),
        sku: t('manage-products-edit-component.variants.header.sku'),
        price: t('manage-products-edit-component.variants.header.price'),
        discount: t('manage-products-edit-component.variants.header.discount'),
        quantity: t('manage-products-edit-component.variants.header.quantity'),
        actions: t('manage-products-edit-component.variants.header.actions'),
        deletePopupTitle: t('manage-products-edit-component.variants.delete-popup.title'),
        deletePopupDescription: t('manage-products-edit-component.variants.delete-popup.description'),
        deletePopupWarning: t('manage-products-edit-component.variants.delete-popup.warning'),
        successfullyDeleted: t('manage-products-edit-component.variants.delete-success'),
    };

    // uuid of variant being removed
    const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);

    // uuids of successfully removed variants
    // to prevent having to call product detail service again
    // and possibly overriding changes the user made on other inputs
    const [removed, setRemoved] = useState<string[]>([]);

    const variantsToDisplay =
        myAccountProductDetail?.data?.variants
            // hide successfully removed variants
            ?.filter((variant) => variant.uuid && !removed.includes(variant.uuid)) || [];

    // dispatch notification, according to feedback of service
    useEffect(() => {
        if (lastResponseTime) {
            const removingUuid = showDeleteModal;

            // close delete modal
            showDeleteModal !== null && setShowDeleteModal(null);

            if (errorDelete) {
                showDefaultNotification({ type: 'error', message: errorDelete });
            } else {
                showDefaultNotification({ type: 'success', message: translations.successfullyDeleted });
                // remove variant from table
                removingUuid && setRemoved((previous) => [...previous, removingUuid]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    const handleOnConfirmDelete: () => void = () => {
        showDeleteModal && requestDelete({ uuid: showDeleteModal });
    };

    return (
        <>
            {variantsToDisplay.length > 0 && (
                <>
                    <div className="d-flex flex-column gap-4 mb-2">
                        <div className="row">
                            <div className="col-12">
                                <Table
                                    borderless
                                    headerCols={[
                                        { classes: 'text-start', text: translations.attributes },
                                        { classes: 'text-start', text: translations.sku },
                                        { classes: 'text-end', text: translations.price },
                                        { classes: 'text-end', text: translations.discount },
                                        { classes: 'text-end', text: translations.quantity },
                                        { classes: 'text-end', text: translations.actions },
                                    ]}
                                    contentRows={variantsToDisplay.map((variant) => {
                                        return [
                                            { classes: 'text-start', content: variant.attributes?.join(', ') || '-' },
                                            { classes: 'text-start', content: variant.sku || '-' },
                                            { classes: 'text-end', content: `${formatPrice(variant.price) || '-'}` },
                                            { classes: 'text-end', content: `${formatPrice(variant.discount_price) || '-'}` },
                                            { classes: 'text-end', content: `${variant.quantity || '-'}` },
                                            {
                                                classes: 'text-end',
                                                content: (
                                                    <Table.Options
                                                        options={[
                                                            <button
                                                                type="button"
                                                                disabled={loading}
                                                                onClick={() => setShowDeleteModal(variant.uuid || null)}>
                                                                <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                                    <i className="fa-light fa-trash-can" />
                                                                </Typography>
                                                            </button>,
                                                        ]}
                                                    />
                                                ),
                                            },
                                        ];
                                    })}
                                />
                            </div>
                        </div>
                    </div>

                    {/* popup to confirm deletion of variant */}
                    <PopupDelete
                        question={translations.deletePopupTitle}
                        confirmation={translations.deletePopupDescription}
                        warning={translations.deletePopupWarning}
                        onClickConfirm={handleOnConfirmDelete}
                        show={showDeleteModal !== null}
                        onHide={() => setShowDeleteModal(null)}
                    />
                </>
            )}
        </>
    );
};

const ImagesTable: FC = () => {
    const { showDefaultNotification } = useNotifications();
    const { myAccountProductDetail } = useData();
    const { request: requestDelete, loading, error: errorDelete, lastResponseTime } = useDeleteProductImage();
    const { request: requestMyAccountProductDetail } = useGetMyAccountProductDetail();
    const { t } = useTranslation();

    const translations = {
        image: t('manage-products-edit-component.images.header.image'),
        actions: t('manage-products-edit-component.images.header.actions'),
        deletePopupTitle: t('manage-products-edit-component.images.delete-popup.title'),
        deletePopupDescription: t('manage-products-edit-component.images.delete-popup.description'),
        deletePopupWarning: t('manage-products-edit-component.images.delete-popup.warning'),
        successfullyDeleted: t('manage-products-edit-component.images.delete-success'),
    };

    // uuid of image being removed
    const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);

    // dispatch notification, according to feedback of removal of image
    useEffect(() => {
        if (lastResponseTime) {
            // close delete modal
            showDeleteModal !== null && setShowDeleteModal(null);

            if (errorDelete) {
                showDefaultNotification({ type: 'error', message: errorDelete });
            } else {
                showDefaultNotification({ type: 'success', message: translations.successfullyDeleted });
                // get updated images
                const productUuid = myAccountProductDetail?.data?.uuid;
                requestMyAccountProductDetail({ uuid: productUuid || '' });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    const handleOnConfirmDelete: () => void = () => {
        showDeleteModal && requestDelete({ uuid: showDeleteModal });
    };

    return (
        <>
            {myAccountProductDetail?.data?.images && myAccountProductDetail.data.images.length > 0 && (
                <>
                    <Table
                        borderless
                        headerCols={[
                            { classes: 'text-start', text: translations.image },
                            { classes: 'text-end', text: translations.actions },
                        ]}
                        contentRows={myAccountProductDetail.data.images.map((image) => {
                            return [
                                {
                                    classes: 'text-start',
                                    content: (
                                        <img
                                            className={styles['product-image']}
                                            src={image.src}
                                            alt={myAccountProductDetail.data?.product?.name}
                                        />
                                    ),
                                },
                                {
                                    classes: 'text-end',
                                    content: (
                                        <Table.Options
                                            options={[
                                                <button
                                                    type="button"
                                                    disabled={loading}
                                                    onClick={() => setShowDeleteModal(image.uuid || null)}>
                                                    <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                        <i className="fa-light fa-trash-can" />
                                                    </Typography>
                                                </button>,
                                            ]}
                                        />
                                    ),
                                },
                            ];
                        })}
                    />

                    {/* popup to confirm deletion of image */}
                    <PopupDelete
                        question={translations.deletePopupTitle}
                        confirmation={translations.deletePopupDescription}
                        warning={translations.deletePopupWarning}
                        onClickConfirm={handleOnConfirmDelete}
                        show={showDeleteModal !== null}
                        onHide={() => setShowDeleteModal(null)}
                    />
                </>
            )}
        </>
    );
};
